// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Inventory2Outlined,
  SettingsOutlined,
  ShoppingCartOutlined,
} from '@/constants/hardfin-standard-icons';
import { ComponentType } from 'react';
import { z } from 'zod';

/**
 * Item types for the unified Item Catalog.
 *
 * These map to backend classification enums:
 * - SERVICE = NON_PHYSICAL_PRODUCT
 * - DEVICE = SERIALIZED_PRODUCT
 * - BULK = BATCH_PART
 */
export const itemTypeSchema = z.enum(['SERVICE', 'DEVICE', 'BULK']);

export type ItemType = z.infer<typeof itemTypeSchema>;

/**
 * Filter type including "all" option for the items list.
 */
export type ItemTypeFilter = ItemType | 'all';

/**
 * Mapping from ItemType to backend ClassificationType enum values.
 */
export const ITEM_TYPE_TO_CLASSIFICATION: Record<ItemType, string> = {
  SERVICE: 'NON_PHYSICAL_PRODUCT',
  DEVICE: 'SERIALIZED_PRODUCT',
  BULK: 'BATCH_PART',
};

/**
 * Human-readable labels for item types.
 */
export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  SERVICE: 'Service',
  DEVICE: 'Device',
  BULK: 'Bulk',
};

/**
 * Short descriptions for each item type.
 */
export const ITEM_TYPE_DESCRIPTIONS: Record<ItemType, string> = {
  SERVICE:
    'Service items are non-physical items that do not have countable inventory (such as software or professional services)',
  DEVICE:
    'Device items are products associated with individual tracked assets (create a serialized asset to use a device item)',
  BULK: 'Bulk items are countable parts that are measured by quantity but not as individual assets (unit counts can be recorded in bulk)',
};

/**
 * Example items for each type.
 */
export const ITEM_TYPE_EXAMPLES: Record<ItemType, string> = {
  SERVICE: 'Software license, cloud access, data fees, consulting hours',
  DEVICE: 'Custom equipment, proprietary devices, laptops, phones',
  BULK: 'Cables, chargers, RAM modules, spare screws',
};

interface IconProps {
  color?:
    | 'action'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'disabled';
  fontSize?: 'small' | 'medium' | 'large' | 'inherit';
}

/**
 * Icons for each item type.
 */
export const ITEM_TYPE_ICONS: Record<ItemType, ComponentType<IconProps>> = {
  SERVICE: ShoppingCartOutlined,
  DEVICE: Inventory2Outlined,
  BULK: SettingsOutlined,
};

/**
 * Mapping from category query param to ItemType.
 */
export const CATEGORY_TO_ITEM_TYPE: Record<string, ItemType> = {
  service: 'SERVICE',
  device: 'DEVICE',
  bulk: 'BULK',
};

/**
 * Category query param values (lowercase item types).
 */
type ItemCategory = 'service' | 'device' | 'bulk';

/**
 * Mapping from ItemType to category query param value.
 */
export const ITEM_TYPE_TO_CATEGORY: Record<ItemType, ItemCategory> = {
  SERVICE: 'service',
  DEVICE: 'device',
  BULK: 'bulk',
};
